import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { productsService } from '../services/products.service';
import { useAuth } from './AuthContext';

interface Product {
  id: number;
  name: string;
  slug: string;
}

interface ProductContextType {
  products: Product[];
  selectedProductId: number | null;
  setSelectedProductId: (id: number | null) => void;
  isLoading: boolean;
}

const ProductContext = createContext<ProductContextType | null>(null);

export function ProductProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(() => {
    const stored = localStorage.getItem('crm-selected-product');
    return stored ? Number(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsLoading(false);
      return;
    }

    // Use products from login response if available
    const userProducts = (user as any)?.products;
    if (userProducts && Array.isArray(userProducts) && userProducts.length > 0) {
      const prods = userProducts
        .map(
          (up: any) => up.product || { id: up.productId, name: `Product ${up.productId}`, slug: '' }
        )
        .filter(Boolean);
      if (prods.length > 0 && prods[0].name) {
        setProducts(prods);
        if (!selectedProductId) {
          setSelectedProductId(prods[0].id);
        }
      }
    }

    productsService
      .list()
      .then((res: any) => {
        const prods = Array.isArray(res) ? res : res?.data || [];
        if (prods.length > 0) {
          setProducts(prods);
          if (!selectedProductId) {
            setSelectedProductId(prods[0].id);
          }
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (selectedProductId) {
      localStorage.setItem('crm-selected-product', String(selectedProductId));
    } else {
      localStorage.removeItem('crm-selected-product');
    }
  }, [selectedProductId]);

  return (
    <ProductContext.Provider
      value={{ products, selectedProductId, setSelectedProductId, isLoading }}
    >
      {children}
    </ProductContext.Provider>
  );
}

export function useProduct() {
  const ctx = useContext(ProductContext);
  if (!ctx) {
    throw new Error('useProduct must be used within ProductProvider');
  }
  return ctx;
}
